import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup'

import { fetchProjectTemplates } from 'api/projectTemplates'
import { useControlledForm, useDebounce, useInfiniteScroll } from 'hooks'
import { getSearchDefaultValues } from './model'
import { getSearchSchema } from './schema'

const searchDefaultValues = getSearchDefaultValues()

const DEFAULT_LIMIT = 25

type TemplatesResponse = {
  count: number | null
  next: string | null
  previous: string | null
  results: any[]
}

const DEFAULT_RESPONSE: TemplatesResponse = {
  count: null,
  next: null,
  previous: null,
  results: [],
}

const useFormState = setSearch => {
  const searchSchema = getSearchSchema()

  const { control, handleFormSubmit, watch } = useControlledForm<{
    search?: string
  }>({
    defaultValues: searchDefaultValues,
    resolver: yupResolver(searchSchema),
    handleFormData: data => setSearch(data?.search),
  })

  React.useEffect(() => {
    // submits the search on every change; handleSubmit ignores its argument
    // unless it is an event
    const subscription = watch(() => {
      handleFormSubmit()
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [handleFormSubmit, watch])

  return { control }
}

export const useInternalState = (state, handlers) => {
  const [offset, setOffset] = React.useState(0)
  const [search, setSearch] = React.useState('')
  const [data, setData] = React.useState(DEFAULT_RESPONSE)
  const [isPending, setIsPending] = React.useState(false)

  const { activeTemplate, isOpen } = state
  const { onBack, onCancel, onClose, onConfirm, setActiveTemplate } = handlers

  const debouncedSearch = useDebounce(search)

  const handleActiveTemplate = React.useCallback(
    template => setActiveTemplate(template),
    [setActiveTemplate],
  )

  const handleOffset = React.useCallback(
    offset => {
      setOffset(prevState =>
        offset != null ? offset : prevState + DEFAULT_LIMIT,
      )
    },
    [setOffset],
  )

  const handleBack = React.useCallback(onBack, [onBack])

  const handleCancel = React.useCallback(onCancel, [onCancel])

  const handleClose = React.useCallback(onClose, [onClose])

  const handleConfirm = React.useCallback(onConfirm, [onConfirm])

  React.useEffect(() => {
    if (debouncedSearch || (debouncedSearch ?? '').length === 0 || isOpen) {
      handleOffset(0)
      setData(DEFAULT_RESPONSE)
    }
  }, [debouncedSearch, handleOffset, isOpen])

  React.useEffect(() => {
    const hasNoActiveTemplate = activeTemplate == null && data?.results != null
    const hasNoResults = (data?.results?.length ?? 0) === 0 && isOpen

    if (hasNoActiveTemplate || hasNoResults) {
      handleActiveTemplate(data?.results[0])
    }
  }, [activeTemplate, data, handleActiveTemplate, isOpen])

  React.useEffect(() => {
    const fetchJobTemplates = async () => {
      setIsPending(true)

      const { response } = await fetchProjectTemplates({
        limit: DEFAULT_LIMIT,
        offset,
        search: debouncedSearch,
      })

      setData(prevState => ({
        ...prevState,
        ...response,
        results: [...prevState?.results, ...response?.results],
      }))
      setIsPending(false)
    }

    if (isOpen) {
      fetchJobTemplates()
    }
  }, [debouncedSearch, offset, isOpen])

  const { control } = useFormState(setSearch)
  const lastElementRef = useInfiniteScroll(
    { hasNextPage: data?.next },
    { handleOffset },
  )

  return {
    lastElementRef,
    search: { control },
    templates: data?.results,
    handleActiveTemplate,
    handleBack,
    handleCancel,
    handleClose,
    handleConfirm,
    isPending,
  }
}
